import type { CaseEndpoint } from '../../types'
import type { EndpointRelationship } from '../../types/case-container/relationships'
import type { useInfrastructureMap } from '../../hooks/use-infrastructure-map'
import type { useCollectionSet } from '../../hooks'
import type { CanvasState } from './infrastructure-map-canvas.state'
import {
  usePanZoom,
  useNodeSelection,
  useNodeDragHandlers,
  useContextMenuActions,
} from './infrastructure-map-canvas.hooks'
import { useCanvasHandlers } from './infrastructure-map-canvas.handlers'
import { useCanvasDerived } from './infrastructure-map-canvas.derived'

type InfraMapApi = ReturnType<typeof useInfrastructureMap>
type CollectionSetApi = ReturnType<typeof useCollectionSet>

interface MapCanvasOrchestratorArgs {
  state: CanvasState
  infraMap: InfraMapApi
  collection: CollectionSetApi
  endpoints: CaseEndpoint[]
  relationships: EndpointRelationship[]
  parentChildMap: Map<string, string[]>
}

export interface MapCanvasOrchestrator {
  panZoom: ReturnType<typeof usePanZoom>
  selection: ReturnType<typeof useNodeSelection>
  dragHandlers: ReturnType<typeof useNodeDragHandlers>
  handlers: ReturnType<typeof useCanvasHandlers>
  contextActions: ReturnType<typeof useContextMenuActions>
  derived: ReturnType<typeof useCanvasDerived>
}

export function useMapCanvasOrchestrator({
  state,
  infraMap,
  collection,
  endpoints,
  relationships,
  parentChildMap,
}: MapCanvasOrchestratorArgs): MapCanvasOrchestrator {
  const panZoom = usePanZoom(
    {
      viewport: state.viewport,
      setViewport: state.setViewport,
      isPanning: state.isPanning,
      setIsPanning: state.setIsPanning,
      panStart: state.panStart,
      setPanStart: state.setPanStart,
    },
    { selectedEndpointIds: state.selectedEndpointIds, displayInfo: infraMap.displayInfo }
  )

  const selection = useNodeSelection(
    state.setSelectedEndpointIds,
    state.lastClickedIdRef,
    infraMap
  )

  const dragHandlers = useNodeDragHandlers(state.setDragPositions, infraMap)

  const handlers = useCanvasHandlers({
    relationships,
    isInSet: collection.isInSet,
    displayInfo: infraMap.displayInfo,
    linkCreation: infraMap.linkCreation,
    mapSet: infraMap.mapSet,
    selectedEndpointIds: state.selectedEndpointIds,
    setSelectedEndpointIds: state.setSelectedEndpointIds,
    setNodeContextMenu: state.setNodeContextMenu,
    setLinkContextMenu: state.setLinkContextMenu,
    setEditLinkId: state.setEditLinkId,
    linkContextMenu: state.linkContextMenu,
  })

  const contextActions = useContextMenuActions(
    {
      nodeContextMenu: state.nodeContextMenu,
      setNodeContextMenu: state.setNodeContextMenu,
      setDetailsEndpointId: state.setDetailsEndpointId,
    },
    {
      infraMap,
      collectionSet: {
        addToSet: collection.addToSet,
        removeFromSet: collection.removeFromSet,
        addMultiple: collection.addMultiple,
      },
      selectedEndpointIds: state.selectedEndpointIds,
    }
  )

  const derived = useCanvasDerived({
    endpoints,
    relationships,
    parentChildMap,
    dragPositions: state.dragPositions,
    infraMap,
  })

  return { panZoom, selection, dragHandlers, handlers, contextActions, derived }
}
